// This if strictly for development & debugging only.
// Do not use `window.devtools` from code, only from the console. It's intentionally not exported.
// Ony use the `devtoolsApi` from code to set stuff for `devtools` from the outside. That's the only thing we export from here.

import { cloneDeep } from '@dynamic/ui';
import copyText from 'copy-to-clipboard';

import serviceList from '#/cypress/e2e/utils/serviceList';
import { StorageKeys } from '#/service/constants';
import { Dictionary } from '#/service/utils';

import { consoleIgnores, overrideConsole } from './console';

export interface DevtoolsApi {
  setContexts(contexts: Contexts): void;
  setStoryPageDef: (storyPageDef: any) => void;
  setPluginInfo: (id: string, info: any) => void;
  setLayout: (layout: any) => void;
  overrideConsole: () => void;
  setStore(name: string, store: any, actions: any): void;
  setWdyr(initWdyr: Function): void;
}

declare global {
  interface Window {
    // It's `any` because we don't care about types in the DevTools (browser does not support them anyway)
    devtools?: any;
  }
}

interface Contexts {
  global?: any;
  config?: any;
  auth?: any;
  menu?: any;
  page?: any;
}

// current context of devtools
interface Current {
  storyPageDef?: any;
  contexts: Contexts;
  plugins: Dictionary<string, any>;
  layout?: any;
  stores: Dictionary<string, any>;
  storeActions: Dictionary<string, any>;
}

// copy a string, or object as json to the clipboard.
const copy = (data: any) => {
  if (typeof data !== 'string') {
    data = JSON.stringify(data);
  }
  copyText(data);
};

// flatten nested object, return entries
const flat = (obj: any, keypath = ''): any => {
  if (typeof obj !== 'object') {
    return [[keypath, obj]];
  }

  return Object.entries(obj).reduce((all, [key, value]) => {
    const nextKeypath = [keypath, key].filter(Boolean).join('.');
    const nextLevel = flat(value, nextKeypath);

    return all.concat(nextLevel);
  }, []);
};

const search = (filter?: string) => {
  const pattern = filter && new RegExp(filter, 'i');
  const flatEntries = flat(
    current.stores['translation'].translations
  ) as string[][];

  return Object.fromEntries(
    flatEntries.filter(
      ([key, value]) => !pattern || pattern.test(key) || pattern.test(value)
    )
  );
};

const current: Current = {
  contexts: {},
  plugins: {},
  stores: {},
  storeActions: {},
};

// devtools object that the user can access from the console.
const devtools = {
  // return all the contexts
  context: () => current.contexts,
  // shortcut for getting the config
  config: () => current.contexts.config,
  // shortcut for getting the user
  user: () => current.contexts.global?.user,
  // shortcut for getting one or all feature configs
  // TODO: It only shows Spec JSON feature configs. Nothing from Core Configs is shown here
  feature: (name?: string) =>
    name
      ? current.contexts.config?.features?.[name]
      : current.contexts.config?.features,
  // return one or all stores
  store: (name?: string) => (name ? current.stores[name] : current.stores),
  // return one or all store action objects
  storeActions: (name?: string) =>
    name ? current.storeActions[name] : current.storeActions,
  // copy dynui pagedef to clipboard for putting into storybook.
  // see: one-dynamic-ui/storybook/exports/README.md
  story: () => {
    copy(current.storyPageDef);

    return 'Page definition copied to the clipboard.';
  },
  // navigate to another service
  service: (servicePath: string) => {
    const queryString = serviceList[servicePath];

    if (queryString) {
      window.location.href = window.location.origin + '/' + queryString;
    } else {
      console.error('Invalid servicePath.');
      console.log('Valid:\n' + Object.keys(serviceList).join('\n'));
    }
  },
  // get player info and version
  player: () => {
    const playerStore = current.stores['player'];
    const name = playerStore?.playerName ?? 'unknown';
    const version = playerStore?.pluginApi?.playerVersion?.() ?? 'unknown';

    if (!(name && version)) {
      return 'Player not loaded yet.';
    }

    return `Player name: ${name} - Version: ${version}`;
  },
  // get plugin info
  plugin: (filter?: string) => {
    const pattern = filter && new RegExp(filter, 'i');

    return Object.fromEntries(
      Object.entries(current.plugins).filter(
        ([key]) => !pattern || pattern.test(key)
      )
    );
  },
  // last rendered dynui layout
  layout: () => current.layout,
  translations: (filter?: string) => {
    if (!filter) {
      return {
        ...cloneDeep(current.stores['translation']),
        search,
      };
    }

    return search(filter);
  },
  settings: {
    ads: (enabled?: boolean) => {
      const lsAdsEnabled = localStorage.getItem(StorageKeys.DEVTOOLS_ADS);

      if (enabled === undefined) {
        return console.log(
          `Ads are currently ${
            lsAdsEnabled === 'true' ? 'visible' : 'hidden'
          }. To change it provide an argument (true/false)`
        );
      }

      localStorage.setItem(StorageKeys.DEVTOOLS_ADS, String(enabled));

      console.log(
        'Changes are saved. You may need to reload current page to see them applied'
      );
    },
    locale: (locale?: string) => {
      if (!locale) {
        return localStorage.getItem(StorageKeys.LOCALE);
      }

      localStorage.setItem(StorageKeys.LOCALE, locale);
      console.log(
        'Locale has been updated. You may need to reload current page to see them applied.  Remember that if the locale you set doesnt match one-core locales it will default to fallback.'
      );
    },
    newPlayerControls: (enabled?: boolean) => {
      const isNewPlayerControlsEnabled = localStorage.getItem(
        StorageKeys.DEVTOOLS_PLAYER_CONTROLS
      );

      if (enabled === undefined) {
        return console.log(
          `New player controls are currently ${
            isNewPlayerControlsEnabled === 'true' ? 'in use' : 'ignored'
          }. To change it provide an argument (true/false)`
        );
      }

      localStorage.setItem(
        StorageKeys.DEVTOOLS_PLAYER_CONTROLS,
        String(enabled)
      );

      console.log(
        'Changes are saved. You may need to reload current page to see them applied'
      );
    },
    wdyr: (enabled?: boolean) => {
      const wdyrEnabled =
        localStorage.getItem(StorageKeys.DEVTOOLS_WDYR) === 'true';
      const message = (enabled: boolean) =>
        `WhyDidYouRender is ${enabled ? 'enabled' : 'disabled'}.`;

      if (enabled === undefined) {
        return console.log(
          message(wdyrEnabled),
          'To change it, provide an argument (true/false).'
        );
      }

      localStorage.setItem(StorageKeys.DEVTOOLS_WDYR, String(enabled));

      console.log(message(enabled), 'Reload the app to see the change.');
    },
    consoleOverride: (enabled?: boolean) => {
      const storedEnabled =
        localStorage.getItem(StorageKeys.DEVTOOLS_CONSOLE_OVERRIDE) === 'true';
      const message = (enabled: boolean) =>
        `Console overrides are ${enabled ? 'enabled' : 'disabled'}.`;

      if (enabled === undefined) {
        return console.log(
          message(storedEnabled),
          'To change it, provide an argument (true/false).'
        );
      }

      localStorage.setItem(
        StorageKeys.DEVTOOLS_CONSOLE_OVERRIDE,
        String(enabled)
      );

      console.log(message(enabled), 'Reload the app to see the change.');
    },
  },
  consoleIgnores: () => consoleIgnores,
};

// api that the application can use to set stuff in devtools.
// always clone incoming data before storing it to avoid modification from the console or affecting garbage collection.
const devtoolsApi: DevtoolsApi = {
  setContexts(contexts: Contexts) {
    Object.entries(contexts).forEach(([key, value]) => {
      current.contexts[key] = cloneDeep(value);
    });
  },
  setStoryPageDef: (storyPageDef: any) => {
    current.storyPageDef = cloneDeep(storyPageDef);
  },
  setPluginInfo: (id: string, info: any) => {
    current.plugins[id] = cloneDeep(info);
  },
  setLayout: (layout: any) => {
    current.layout = cloneDeep(layout);
  },
  setStore(name: string, store: any, actions: any) {
    // intentionally not cloning to preserve the proxies and allow modification
    current.stores[name] = store;
    current.storeActions[name] = actions;
  },
  setWdyr(initWdyr: Function) {
    if (localStorage.getItem(StorageKeys.DEVTOOLS_WDYR) === 'true') {
      initWdyr();
    }
  },
  overrideConsole,
};

/**
 * Let's use devtools code only in the development mode.
 * Webpack should nicely get rid of unused `devtools` code, so noone will be able to investigate source code of this piece
 */
if (process.env.goal === 'local') {
  window.devtools = devtools;
  console.debug('Devtools available');
}

export default devtoolsApi;
